import { StreetviewOutlined } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import isNil from "lodash/isNil";
import { useEffect, useRef, useState } from "react";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { loadGoogleMaps } from "./loadGoogleMaps";

/**
 * Google Street View panel — interactive panorama via the Maps JS API.
 *
 * Uses StreetViewService.getPanorama() to snap to the nearest panorama
 * within `radius`, then renders an interactive `StreetViewPanorama` the
 * user can drag, pan, and zoom.
 *
 * Save flows (save-as-image, save-to-loan) still use the **Street View
 * Static API** so we get a plain JPEG at the user's current view. On each
 * position/POV change we rebuild the matching Static URL and fire
 * `onViewChange(url)` — PropertyMap stores the latest URL and uses it when
 * the user clicks save, so what they download matches what they're looking
 * at.
 *
 * Requires REACT_APP_GOOGLE_MAPS_KEY with both **Maps JavaScript API** and
 * **Street View Static API** enabled on the Cloud project.
 */
const StreetViewPanel = ({
  address,
  height = "200px",
  heading = 0,
  pitch = 0,
  radius = 1000,
  onViewChange,
  sx,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const latitude = address?.latitude;
  const longitude = address?.longitude;

  const containerRef = useRef(null);
  const panoramaRef = useRef(null);
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);

  const [status, setStatus] = useState("loading"); // loading | ready | no-imagery | error
  const [imageDate, setImageDate] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!apiKey) {
      setStatus("error");
      return;
    }
    if (isNil(latitude) || isNil(longitude)) {
      setStatus("no-imagery");
      return;
    }

    setStatus("loading");

    loadGoogleMaps()
      .then((google) => {
        if (cancelled) return;
        const service = new google.maps.StreetViewService();
        service.getPanorama(
          {
            location: { lat: latitude, lng: longitude },
            radius,
            source: google.maps.StreetViewSource.OUTDOOR,
          },
          (data, svStatus) => {
            if (cancelled) return;
            if (svStatus !== "OK" || !data?.location?.pano) {
              console.warn(
                "[StreetViewPanel] getPanorama returned",
                svStatus,
                "for",
                { latitude, longitude, radius },
              );
              setStatus("no-imagery");
              return;
            }
            if (!containerRef.current) {
              console.error(
                "[StreetViewPanel] Panorama container ref was unmounted before init could run",
              );
              setStatus("error");
              return;
            }

            let panorama;
            try {
              panorama = new google.maps.StreetViewPanorama(
                containerRef.current,
                {
                  pano: data.location.pano,
                  pov: { heading, pitch },
                  zoom: 1,
                  addressControl: false,
                  fullscreenControl: false,
                  motionTracking: false,
                  motionTrackingControl: false,
                  showRoadLabels: false,
                },
              );
            } catch (err) {
              console.error(
                "[StreetViewPanel] StreetViewPanorama constructor threw:",
                err,
              );
              setStatus("error");
              return;
            }
            panoramaRef.current = panorama;
            const resolvedDate =
              data.imageDate || data.location?.imageDate || null;
            console.log("[StreetViewPanel] getPanorama data:", data);
            setImageDate(resolvedDate);
            setStatus("ready");

            const emit = () => {
              if (!onViewChangeRef.current) return;
              const pos = panorama.getPosition();
              const pov = panorama.getPov() || {};
              if (!pos) return;
              const zoom = panorama.getZoom();
              const fov = Math.max(
                10,
                Math.min(120, 180 / Math.pow(2, isNil(zoom) ? 1 : zoom)),
              );
              const url = buildStreetViewUrl({
                apiKey,
                latitude: pos.lat(),
                longitude: pos.lng(),
                heading: pov.heading || 0,
                pitch: pov.pitch || 0,
                fov,
              });
              onViewChangeRef.current(url);
            };
            emit();
            panorama.addListener("position_changed", emit);
            panorama.addListener("pov_changed", emit);
            panorama.addListener("zoom_changed", emit);
            panorama.addListener("pano_changed", () => {
              const panoId = panorama.getPano();
              if (!panoId) return;
              service.getPanorama({ pano: panoId }, (d, s) => {
                if (cancelled) return;
                if (s === "OK") setImageDate(d?.imageDate || null);
              });
            });
          },
        );
      })
      .catch((err) => {
        console.error("[StreetViewPanel] Failed to load Maps JS API:", err);
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (panoramaRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(panoramaRef.current);
      }
      panoramaRef.current = null;
    };
    // heading/pitch are initial values only — don't re-init the panorama as
    // the user drags. Position, apiKey, and radius are true inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, latitude, longitude, radius]);

  if (status === "error" || status === "no-imagery") {
    const reason = !apiKey
      ? "Street view key not configured"
      : status === "no-imagery"
        ? "No street view imagery available"
        : "Street view failed to load";
    return (
      <Flx
        center
        column
        sx={{
          height,
          background: "#f5f5f5",
          gap: 1,
          ...sx,
        }}
      >
        <StreetviewOutlined sx={{ color: "#666", fontSize: "2rem" }} />
        <Txt sx={{ color: "#666" }}>{reason}</Txt>
      </Flx>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height,
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {status === "ready" && imageDate && (
        <Box
          sx={{
            position: "absolute",
            top: 44,
            left: 8,
            zIndex: 5,
            background: "rgba(0,0,0,0.65)",
            color: "#fff",
            px: 1,
            py: 0.25,
            borderRadius: "4px",
            fontSize: "0.7rem",
            lineHeight: 1.4,
            pointerEvents: "none",
          }}
        >
          Captured: {formatImageDate(imageDate)}
        </Box>
      )}
      {status === "loading" && (
        <Flx
          center
          sx={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            pointerEvents: "none",
          }}
        >
          <CircularProgress size={24} />
        </Flx>
      )}
    </Box>
  );
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatImageDate = (raw) => {
  if (!raw) return "";
  const match = /^(\d{4})(?:-(\d{1,2}))?/.exec(String(raw));
  if (!match) return String(raw);
  const year = match[1];
  const monthIdx = match[2] ? parseInt(match[2], 10) - 1 : null;
  if (monthIdx === null || monthIdx < 0 || monthIdx > 11) return year;
  return `${MONTHS[monthIdx]} ${year}`;
};

/**
 * Build a Street View Static API URL. Used by the panorama to snapshot the
 * current view on pan, and by PropertyMap as a fallback before the
 * panorama finishes initializing.
 */
export const buildStreetViewUrl = ({
  apiKey,
  latitude,
  longitude,
  fullAddress,
  heading = 0,
  pitch = 0,
  fov = 90,
  radius = 1000,
  size = "1280x1280",
}) => {
  if (!apiKey) return null;
  const hasCoords = !isNil(latitude) && !isNil(longitude);
  const location = hasCoords ? `${latitude},${longitude}` : fullAddress || null;
  if (!location) return null;

  const params = new URLSearchParams({
    key: apiKey,
    size,
    location,
    heading: String(heading),
    pitch: String(pitch),
    fov: String(fov),
    return_error_code: "true",
  });
  if (hasCoords) params.set("radius", String(radius));
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
};

export default StreetViewPanel;
